/**
 * My Websites - Manage created websites
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Globe, Plus, Eye, Trash2, ExternalLink, Edit,
  FileText, Link2, Layout, CheckCircle, Clock,
  Copy, BarChart3
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  landing_page: { icon: FileText, label: 'Landing Page', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  bio_page: { icon: Link2, label: 'Bio Page', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  multi_page: { icon: Layout, label: 'Multi-Page', color: 'text-teal-400', bg: 'bg-teal-500/20' }
};

export default function MyWebsites() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && token) {
      fetchSites();
    }
  }, [user, token]);

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/website-builder/my-sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data.sites || []);
    } catch (err) {
      console.error('Failed to fetch sites:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteSite = async (siteId) => {
    if (!confirm('Are you sure you want to delete this site?')) return;
    
    try {
      await axios.delete(`${API}/website-builder/site/${siteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(sites.filter(s => s.id !== siteId));
    } catch (err) {
      console.error('Failed to delete site:', err);
    }
  };

  const publishSite = async (siteId) => {
    try {
      const response = await axios.post(`${API}/website-builder/site/${siteId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Site published! URL: ${response.data.public_url}`);
      fetchSites();
    } catch (err) {
      console.error('Failed to publish site:', err);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(window.location.origin + url);
    alert('URL copied to clipboard!');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <Globe className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Sign in to view your websites</h2>
          <Button onClick={() => navigate('/auth')} className="bg-teal-500 hover:bg-teal-600 mt-4">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">My Websites</h1>
            <p className="text-zinc-400">Manage your landing pages, bio pages, and sites</p>
          </div>
          <Button 
            onClick={() => navigate('/website-builder')}
            className="bg-teal-500 hover:bg-teal-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Site
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">Loading sites...</div>
        ) : sites.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center"
          >
            <Globe className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">No Websites Yet</h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              Create your first website with AI-generated content. Choose from landing pages, bio pages, or full multi-page sites.
            </p>
            <Button 
              onClick={() => navigate('/website-builder')}
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Site
            </Button>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sites.map((site, index) => {
              const typeConfig = TYPE_CONFIG[site.type] || TYPE_CONFIG.landing_page;
              const Icon = typeConfig.icon;
              
              return (
                <motion.div
                  key={site.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-colors"
                >
                  {/* Preview Header */}
                  <div 
                    className="h-32 relative"
                    style={{ backgroundColor: site.primary_color + '20' }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon className="w-12 h-12" style={{ color: site.primary_color }} />
                    </div>
                    
                    {/* Status Badge */}
                    <div className="absolute top-3 right-3">
                      {site.published ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">
                          <CheckCircle className="w-3 h-3" />
                          Live
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">
                          <Clock className="w-3 h-3" />
                          Draft
                        </span>
                      )}
                    </div>
                    
                    {/* Type Badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${typeConfig.bg} ${typeConfig.color}`}>
                        {typeConfig.label}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-white font-semibold mb-1">
                      {site.business_name || site.name || 'Untitled Site'}
                    </h3>
                    <p className="text-zinc-500 text-sm mb-4 line-clamp-2">
                      {site.business_description || site.bio || site.title || 'No description'}
                    </p>
                    
                    {/* Stats */}
                    {site.type === 'bio_page' && (
                      <div className="flex items-center gap-4 text-xs text-zinc-500 mb-4">
                        <span>{site.links?.length || 0} links</span>
                        <span>{site.views || 0} views</span>
                      </div>
                    )}
                    
                    {site.type === 'landing_page' && (
                      <div className="flex items-center gap-4 text-xs text-zinc-500 mb-4">
                        <span>{site.sections?.length || 0} sections</span>
                        <span className="capitalize">{site.style} style</span>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {/* Preview button */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`/s/${site.public_url?.split('/s/')[1] || site.id}`, '_blank')}
                        className="border-zinc-700 text-zinc-400 hover:text-white"
                        title="Preview site"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Preview
                      </Button>

                      {site.published && site.public_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyUrl(site.public_url)}
                          className="border-zinc-700 text-zinc-400 hover:text-white"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy URL
                        </Button>
                      )}
                      
                      {!site.published && (
                        <Button
                          size="sm"
                          onClick={() => publishSite(site.id)}
                          className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        >
                          Publish
                        </Button>
                      )}
                      
                      <div className="flex-1" />
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteSite(site.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
